import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";
import { Subscription } from "rxjs";

import { CheckboxGroupComponent } from "./checkbox-group.component";
import { CheckboxComponent } from "./checkbox.component";

@Component({
  selector: "ta-checkbox-toggle",
  template: `
    <ta-checkbox
      [disabled]="isDisabled"
      [(checked)]="checked"
      [(indeterminate)]="indeterminate"
      class="checkbox-toggle"
      [class.checkbox-toggle--toggle-all]="toggleAll"
    >
      <ng-content></ng-content>
    </ta-checkbox>
  `
})
export class CheckboxToggleComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(CheckboxComponent, { static: true }) childCheckbox: CheckboxComponent;

  @Input()
  toggle: string;

  @Input()
  get toggleAll(): boolean {
    return this._toggleAll;
  }
  set toggleAll(value: boolean) {
    this._toggleAll = value != null && value !== false;
  }
  private _toggleAll: boolean = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value != null && value !== false;
  }
  private _disabled: boolean = false;

  get isDisabled(): boolean {
    return this.disabled || this.parent.disabled;
  }

  @Input()
  get checked(): boolean {
    return this._checked;
  }
  set checked(value: boolean) {
    value = value != null && value !== false;
    if (this._checked === value) {
      return;
    }
    this._checked = value;
    this.checkedChange.emit(value);
  }
  private _checked: boolean = false;
  @Output() checkedChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  get indeterminate(): boolean {
    return this._indeterminate;
  }
  set indeterminate(value: boolean) {
    value = value != null && value !== false;
    if (this._indeterminate === value) {
      return;
    }
    this._indeterminate = value;
    this.indeterminateChange.emit(value);
  }
  private _indeterminate: boolean = false;
  indeterminateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(private parent: CheckboxGroupComponent<any>) {}

  ngOnInit(): void {
    this.parent.addCheckboxToggle(this);
  }

  private parentValueChangeSubscription?: Subscription;
  private checkedChangeSubscription?: Subscription;
  private indeterminateChangeSubscription?: Subscription;
  ngAfterViewInit(): void {
    this.parentValueChangeSubscription = this.parent.valueChange.subscribe(
      () => {
        this.updateState();
      }
    );

    this.checkedChangeSubscription = this.checkedChange.subscribe(() => {
      if (this.indeterminate) {
        return;
      }
      if (this.toggleAll) {
        this.parent.toggleAll(this.checked);
      } else {
        this.parent.toggleGroup(this.toggle, this.checked);
      }
    });
    this.indeterminateChangeSubscription = this.indeterminateChange.subscribe(
      () => {
        if (this.indeterminate || this.checked) {
          return;
        }
        if (this.toggleAll) {
          this.parent.toggleAll(false);
        } else {
          this.parent.toggleGroup(this.toggle, false);
        }
      }
    );
  }

  ngOnDestroy(): void {
    if (this.parentValueChangeSubscription != null) {
      this.parentValueChangeSubscription.unsubscribe();
    }
    if (this.checkedChangeSubscription != null) {
      this.checkedChangeSubscription.unsubscribe();
    }
    if (this.indeterminateChangeSubscription != null) {
      this.indeterminateChangeSubscription.unsubscribe();
    }

    this.parent.removeCheckboxToggle(this);
  }

  /**
   * @internal
   */
  updateState(): void {
    let state: boolean | null = this.getToggleGroupState();
    let newChecked: boolean, newIndeterminate: boolean;
    if (state == null) {
      newIndeterminate = true;
      newChecked = false;
    } else {
      newIndeterminate = false;
      newChecked = state;
    }

    let checkedChange: boolean = this._checked !== newChecked,
      indeterminateChanged: boolean = this._indeterminate !== newIndeterminate;
    this._checked = newChecked;
    this._indeterminate = newIndeterminate;

    if (checkedChange) {
      this.checkedChange.emit(newChecked);
    }
    if (indeterminateChanged) {
      this.indeterminateChange.emit(newIndeterminate);
    }
  }

  private getToggleGroupState(): boolean | null {
    if (this.toggleAll) {
      // toggle all state
      if (this.parent.value.length === this.parent.checkboxes.size) {
        // TODO: do this check better, are we guaranteeing this?
        return true;
      }
      if (this.parent.value.length === 0) {
        return false;
      }
      return null;
    }

    let checkboxValues: any[] = Array.from(this.parent.checkboxes)
      .filter((checkbox) => {
        return checkbox.isInToggleGroup(this.toggle);
      })
      .map((checkbox) => {
        return checkbox.value;
      });

    if (checkboxValues.length === 0) {
      // no checkboxes included, consider it unchecked
      return false;
    }

    let isFirstChecked: boolean = this.parent.isSelected(checkboxValues[0]);
    let isIndeterminate: boolean = checkboxValues.some((value) => {
      return this.parent.isSelected(value) !== isFirstChecked;
    });
    if (isIndeterminate) {
      return null;
    }
    return isFirstChecked;
  }
}
